/** @format */

import { element } from 'protractor';
/** @format */
import { Inject, Injectable } from '@angular/core';
import { Observable, combineLatest, defer, of, pipe, from } from 'rxjs';
import { map, tap, take, switchMap, first, filter } from 'rxjs/operators';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
  DocumentChangeAction,
  DocumentSnapshotDoesNotExist,
  DocumentSnapshotExists,
  Action,
} from '@angular/fire/firestore';
import * as firebase from 'firebase';

//doc$ 으로 불러온 field값에 다른값 엮을때
export const docJoin = (afs: AngularFirestore, field: string, collection: string) => {
  return source =>
    defer(() => {
      let parent;
      return source.pipe(
        switchMap(data => {
          parent = data;

          const id = data[field];

          return afs
            .doc(`${collection}/${id}`)
            .snapshotChanges()
            .pipe(
              map(doc => {
                return doc.payload.data();
              })
            );
        }),
        map(fieldData => {
          delete parent[field];
          return { [field]: fieldData, ...parent };
        })
      );
    });
};

export const listJoin = (afs: AngularFirestore, field: string, collection: string) => {
  return source =>
    defer(() => {
      let parent;
      const keys = Object.keys(field);
      return source.pipe(
        switchMap(async data => {
          // Save the parent data state
          parent = data;
          if (!parent || typeof parent == 'undefined') {
            return of([]);
          }
          let result = [];
          if (parent) {
            for (const doc of parent) {
              const reads$ = [];
              const arr = doc[field];
              for (const id of arr) {
                const docs$ = await afs
                  .doc(`${collection}/${id}`)
                  .snapshotChanges()
                  .pipe(
                    map((doc: any) => {
                      const data: any = doc.payload.data();
                      const id = doc.payload.id;
                      return { id: doc.payload.id, ...data };
                    })
                  )
                  .pipe(take(1))
                  .toPromise();

                reads$.push(docs$);
              }
              reads$.length ? reads$ : [];
              result.push({ ...doc, detailList: reads$ });
            }
          }

          return result.length ? result : [];
        }),
        map((data: any) => {
          return data;
        })
      );
    });
};

export const leftJoinDocument = (afs: AngularFirestore, field, collection) => {
  return source =>
    defer(() => {
      // Operator state
      let collectionData;
      let real: any;
      const cache = new Map();
      return source.pipe(
        switchMap(data => {
          real = data;

          // Clear mapping on each emitted val ;
          cache.clear();

          // Save the parent data state
          collectionData = data as any[];

          const reads$ = [];
          let i = 0;
          for (const doc of collectionData) {
            // Skip if doc field does not exist or is already in cache
            if (!doc[field] || cache.get(doc[field])) {
              continue;
            }

            // Push doc read to Array
            reads$.push(afs.collection(collection).doc(doc[field]).valueChanges());
            cache.set(doc[field], i);
            i++;
          }

          return reads$.length ? combineLatest(reads$) : of([]);
        }),
        map((items: any[]) => {
          const datas = items.filter(ele => ele !== undefined && ele !== null);
          return datas && datas.length > 0 ? datas : [];
        }),
        map(joins => {
          return collectionData.map((v, i) => {
            const joinIdx = cache.get(v[field]);
            const item = real.find(e => e.id == v[field]);
            if (field && v[field]) {
              return {
                ...v,
                [field]: { ...joins[joinIdx], id: v[field] } || null,
              };
            } else {
              return v;
            }
          });
        })
      );
    });
};

export const docListJoin = (afs: AngularFirestore, field: string, collection: string) => {
  return source =>
    defer(() => {
      let parent: any;
      let form;

      const cache = new Map();
      return source.pipe(
        switchMap(async data => {
          // Save the parent data state
          parent = data;
          if (!parent || typeof parent == 'undefined') {
            return of([]);
          }
          let result = [];
          if (parent) {
            const reads$ = [];
            const arr = data[field];

            for (const id of arr) {
              const docs$ = await afs
                .doc(`${collection}/${id}`)
                .snapshotChanges()
                .pipe(
                  map((doc: any) => {
                    const data: any = doc.payload.data();
                    return data;
                  })
                )
                .pipe(take(1))
                .toPromise();

              reads$.push(docs$);
            }
            reads$.length ? reads$ : [];
            result.push({ ...parent, [field]: reads$ });
          }

          return result.length ? result : [];
        }),
        map((data: any) => {
          return data[0];
        })
      );
    });
};

/**
 * {uid : 'test21343242', favoriteUsers : ['test2232','test4453543','sdfwe234234']}
 * user 안에 즐겨찾기한 user id를 Array로 가지고 있고 각각의 유저 정보를 찾아와 이를 엮는 경우와 같으나 처음에 받아올때 Array!
 * [{uid : 'test21343242', favoriteUsers : ['test2232','test4453543','sdfwe234234']}, {uid : 'test2232', favoriteUsers : ['test21343242','test4453543','sdfwe234234']}]
 * @param afs
 * @param field
 * @param collection
 */
export const ArrayleftJoinDocument = (afs: AngularFirestore, field, collection) => {
  return source =>
    defer(() => {
      // Operator state
      let collectionData;
      let real: any;
      const cache = new Map();
      return source.pipe(
        switchMap(data => {
          real = data;
          // Clear mapping on each emitted val ;
          cache.clear();

          // Save the parent data state
          collectionData = data as any[];
          const reads$ = [];
          let i = 0;
          for (const doc of collectionData) {
            // Skip if doc field does not exist or is already in cache
            if (!doc[field]) {
              continue;
            }
            for (const item of doc[field]) {
              if (cache.get(item)) {
                continue;
              }
              // Push doc read to Array
              reads$.push(afs.collection(collection).doc(item).valueChanges());
              cache.set(item, i);
              i++;
            }
          }

          return reads$.length ? combineLatest(reads$) : of([]);
        }),
        map(joins => {
          return collectionData.map(v => {
            const array = [];
            for (const item2 of v[field]) {
              const joinIdx = cache.get(item2);
              array.push({ ...joins[joinIdx], id: item2 });
            }
            if (field) {
              return {
                ...v,
                [field]: array || [],
              };
            } else {
              return v;
            }
          });
        })
      );
    });
};

/**
 * {uid : 'test21343242', favoriteUsers : ['test2232','test4453543','sdfwe234234']}
 * user 안에 즐겨찾기한 user id를 Array로 가지고 있고 각각의 유저 정보를 찾아와 이를 엮는 경우
 * @param afs
 * @param field
 */

export const ArrayJoin = (afs: AngularFirestore, field: string, collection: string) => {
  return source =>
    defer(() => {
      let docData;
      let arr;
      const cache = new Map();

      return source.pipe(
        switchMap(data => {
          cache.clear();

          docData = data; // user(object)

          arr = data[field]; // user.favoriteUsers(array)

          const reads$ = [];
          let i = 0;
          if (arr && arr.length > 0) {
            for (const id of arr) {
              reads$.push(afs.collection(collection).doc(id).valueChanges());

              cache.set(id, i);
              i++;
            }
          }

          return combineLatest(reads$);
        }),

        map(joins => {
          return {
            ...docData,
            [field]: arr.map(v => {
              const joinIdx = cache.get(v); // 고유ID

              return joins[joinIdx] || v;
            }),
          };
        })
      );
    });
};

type CollectionPredicate<T> = string | AngularFirestoreCollection<T>;
type DocPredicate<T> = string | AngularFirestoreDocument<T>;

@Injectable({
  providedIn: 'root',
})
export class DbService {
  public user;
  constructor(@Inject('firebaseProject') public afs: AngularFirestore) {}

  createFsId() {
    return this.afs.createId();
  }

  createdAt() {
    return firebase.default.firestore.FieldValue.serverTimestamp();
  }

  collection$(path, query?) {
    return this.afs
      .collection(path, query)
      .snapshotChanges()
      .pipe(
        map(actions => {
          return actions.map(a => {
            const data: any = a.payload.doc.data();
            const id = a.payload.doc.id;
            return { id, ...data };
          });
        })
      );
  }

  // adminDoc$(path): Observable<any> {
  //   return this.adminFirebase
  //     .doc(path)
  //     .snapshotChanges()
  //     .pipe(
  //       map(doc => {
  //         const data: any = doc.payload.data();
  //         const id = doc.payload.id;
  //         return { id: doc.payload.id, ...data };
  //       })
  //     );
  // }

  doc$(path): Observable<any> {
    return this.afs
      .doc(path)
      .snapshotChanges()
      .pipe(
        map(doc => {
          const data: any = doc.payload.data();
          const id = doc.payload.id;
          return { id: doc.payload.id, ...data };
        })
      );
  }

  doc2$<T>(ref: DocPredicate<T>): Observable<T> {
    return this.doc(ref)
      .snapshotChanges()
      .pipe(
        map((doc: Action<DocumentSnapshotDoesNotExist | DocumentSnapshotExists<T>>) => {
          return doc.payload.data() as T;
        })
      );
  }

  col$<T>(ref: CollectionPredicate<T>, queryFn?): Observable<T[]> {
    return this.col(ref, queryFn)
      .snapshotChanges()
      .pipe(
        map((docs: DocumentChangeAction<T>[]) => {
          return docs.map((a: DocumentChangeAction<T>) => a.payload.doc.data()) as T[];
        })
      );
  }

  col<T>(ref: CollectionPredicate<T>, queryFn?): AngularFirestoreCollection<T> {
    return typeof ref === 'string' ? this.afs.collection<T>(ref, queryFn) : ref;
  }

  doc<T>(ref: DocPredicate<T>): AngularFirestoreDocument<T> {
    return typeof ref === 'string' ? this.afs.doc<T>(ref) : ref;
  }

  /**
   * @param  {string} path 'collection' or 'collection/docID'
   * @param  {object} data new data
   *
   * Creates or updates data on a collection or document.
   **/

  // ** 기본적인 DB처리 **//
  updateAt(path: string, data: Object): Promise<any> {
    const segments = path.split('/').filter(v => v);
    if (segments.length % 2) {
      // Odd is always a collection
      return this.afs.collection(path).add(data);
    } else {
      // Even is always document
      return this.afs.doc(path).set(data, { merge: true });
    }
  }

  /**
   * @param  {string} path path to document
   *
   * Deletes document from Firestore
   **/

  delete(path) {
    return this.afs.doc(path).delete();
  }

  // Helper to format the docId for relationships
  public concatIds(a: string, b: string) {
    return `${a}_${b}`;
  }

  createId() {
    return this.afs.createId();
  }

  //로그인 로그아웃
  loginUser(email, password) {
    return new Promise<any>((resolve, reject) => {
      firebase.default
        .auth()
        .signInWithEmailAndPassword(email, password)
        .then(
          res => {
            localStorage.setItem('userId', res.user.uid);
            resolve(res);
          },
          err => reject(err)
        )
        .catch(err => reject(err));
      // this.redirect();
    });
  }

  logoutUser() {
    return new Promise((resolve, reject) => {
      if (firebase.default.auth().currentUser) {
        firebase.default
          .auth()
          .signOut()
          .then(res => {
            localStorage.clear();
            resolve(res);
          })
          .catch(error => {
            reject(error);
          });
      }
    });
  }
}

///////--------

export const leftJoinOneDocument = (afs: AngularFirestore, field, collection) => {
  return source =>
    defer(() => {
      let parent;

      return source.pipe(
        switchMap(data => {
          // Save the parent data state
          parent = data;

          // Map each path to an Observable
          if (parent[field]) {
            const doc = afs
              .collection(collection)
              .doc(parent[field])
              .snapshotChanges()
              .pipe(
                map(doc => {
                  const data: any = doc.payload.data();
                  const id = doc.payload.id;
                  return { id: doc.payload.id, ...data };
                })
              );

            return doc;
          }
          return of('');
        }),
        map(arr => {
          return { ...parent, [field]: arr };
        })
      );
    });
};
export const leftArrayJoin = (afs: AngularFirestore, field, collection) => {
  return source =>
    defer(() => {
      let parent;

      return source.pipe(
        switchMap(data => {
          parent = data;
          const array = parent[field] as any[];
          const reads$ = [];

          for (const item of array) {
            if (item) {
              const itemData = afs
                .collection(collection)
                .doc(item)
                .snapshotChanges()
                .pipe(
                  map(doc => {
                    const data: any = doc.payload.data();
                    return { id: doc.payload.id, ...data };
                  })
                );

              reads$.push(itemData);
            } else {
              reads$.push(of([]));
            }
          }
          return combineLatest(reads$);
        }),
        map(arr => {
          return { ...parent, [field]: arr };
        })
      );
    });
};
export const getOnePostComments = (afs: AngularFirestore) => {
  return source =>
    defer(() => {
      // Operator state
      let parent;

      return source.pipe(
        switchMap(data => {
          parent = data;

          if (parent) {
            const q = ref => ref.where('postId', '==', parent.id).orderBy('dateCreated', 'desc');
            const doc = afs
              .collection('comments', q)
              .snapshotChanges()
              .pipe(
                map((actions: any) => {
                  return actions.map((a: any) => {
                    const data: any = a.payload.doc.data();
                    const id = a.payload.doc.id;
                    return { id, ...data };
                  });
                })
              );
            return doc;
          }
          return of('');
        }),
        map(arr => {
          return { ...parent, ['comments']: arr };
        })
      );
    });
};

export const getUsersById = (afs: AngularFirestore) => {
  return source =>
    defer(() => {
      // Operator state
      let idArray;
      return source.pipe(
        switchMap(data => {
          // Clear mapping on each emitted val ;

          // Save the parent data state
          idArray = data as any[];

          const reads$ = [];
          if (idArray && idArray.length) {
            for (const userId of idArray) {
              // Push doc read to Array

              if (userId) {
                // Perform query on join key, with optional limit
                const userData = afs
                  .collection('users')
                  .doc(userId)
                  .snapshotChanges()
                  .pipe(
                    map(doc => {
                      const data: any = doc.payload.data();
                      return { id: doc.payload.id, ...data };
                    })
                  );
                reads$.push(userData);
              } else {
                reads$.push(of([]));
              }
            }
            return combineLatest(reads$);
          } else {
            return of([]);
          }
        })
      );
    });
};

export const getReportRefData = (afs: AngularFirestore) => {
  return source =>
    defer(() => {
      // Operator state
      let reportList;

      // Track total num of joined doc reads
      let totalJoins = 0;

      return source.pipe(
        switchMap(data => {
          // Clear mapping on each emitted val ;

          // Save the parent data state
          reportList = data as any[];

          const reads$ = [];
          for (const report of reportList) {
            // Push doc read to Array

            if (report) {
              // Perform query on join key, with optional limit
              if (report.type != '댓글') {
                const postData = afs
                  .collection('posts')
                  .doc(report.refId)
                  .snapshotChanges()
                  .pipe(
                    map(doc => {
                      const data: any = doc.payload.data();
                      return { id: doc.payload.id, ...data };
                    })
                  );
                reads$.push(postData);
              } else {
                const commentData = afs
                  .collection('comments')
                  .doc(report.refId)
                  .snapshotChanges()
                  .pipe(
                    map(doc => {
                      const data: any = doc.payload.data();
                      return { id: doc.payload.id, ...data };
                    })
                  );
                reads$.push(commentData);
              }
            } else {
              reads$.push(of([]));
            }
          }

          // return combineLatest(reads$);
          return reads$.length ? combineLatest(reads$) : of([]);
        }),
        map(joins => {
          return reportList.map((v, i) => {
            totalJoins += joins[i].length;
            return { ...v, ['refData']: joins[i] || null };
          });
        })
      );
    });
};

export const getComments = (afs: AngularFirestore) => {
  return source =>
    defer(() => {
      // Operator state
      let collectionData;

      // Track total num of joined doc reads
      let totalJoins = 0;

      return source.pipe(
        switchMap(data => {
          // Clear mapping on each emitted val ;

          // Save the parent data state
          collectionData = data as any[];
          if (collectionData.length) {
            const reads$ = [];
            for (const doc of collectionData) {
              // Push doc read to Array

              if (doc) {
                // Perform query on join key, with optional limit
                const q = ref => ref.where('postId', '==', doc.id).orderBy('dateCreated', 'desc');
                reads$.push(
                  afs
                    .collection('comments', q)
                    .snapshotChanges()
                    .pipe(
                      map((actions: any) => {
                        return actions.map((a: any) => {
                          const data: any = a.payload.doc.data();
                          const id = a.payload.doc.id;
                          return { id, ...data };
                        });
                      })
                    )
                );
              } else {
                reads$.push(of([]));
              }
            }

            return combineLatest(reads$);
          } else {
            return of([]);
          }
        }),
        map(joins => {
          if (collectionData.length) {
            return collectionData.map((v, i) => {
              totalJoins += joins[i].length;
              return { ...v, ['comments']: joins[i] || null };
            });
          } else {
            return [];
          }
        })
      );
    });
};

export const getAlarmData = (afs: AngularFirestore) => {
  return source =>
    defer(() => {
      // Operator state
      let alarmList;

      // Track total num of joined doc reads
      let totalJoins = 0;

      return source.pipe(
        switchMap(data => {
          // Clear mapping on each emitted val ;

          // Save the parent data state
          alarmList = data as any[];

          const reads$ = [];
          for (const alarm of alarmList) {
            // Push doc read to Array

            if (alarm) {
              // Perform query on join key, with optional limit
              if (alarm.type == 'chat') {
                const chatData = afs
                  .collection('chats')
                  .doc(alarm.typeId)
                  .snapshotChanges()
                  .pipe(
                    map(doc => {
                      const data: any = doc.payload.data();
                      return { id: doc.payload.id, ...data };
                    })
                  );
                reads$.push(chatData);
              } else {
                const commentData = afs
                  .collection('comments')
                  .doc(alarm.typeId2)
                  .snapshotChanges()
                  .pipe(
                    map(doc => {
                      const data: any = doc.payload.data();
                      return { id: doc.payload.id, ...data };
                    })
                  );
                reads$.push(commentData);
              }
            } else {
              reads$.push(of([]));
            }
          }

          return combineLatest(reads$);
        }),
        map(joins => {
          return alarmList.map((v, i) => {
            totalJoins += joins[i].length;
            return { ...v, ['data']: joins[i] || null };
          });
        })
      );
    });
};

export const getLikedPosts = (afs: AngularFirestore) => {
  return source =>
    defer(() => {
      // Operator state
      let likedPosts;

      // Track total num of joined doc reads
      let totalJoins = 0;

      return source.pipe(
        switchMap((data: any) => {
          // Clear mapping on each emitted val ;

          // Save the parent data state
          likedPosts = data.likedPosts;

          const reads$ = [];
          for (const item of likedPosts) {
            // Push doc read to Array

            if (item) {
              const itemData = afs
                .collection('posts')
                .doc(item.postId)
                .snapshotChanges()
                .pipe(
                  map(doc => {
                    const data: any = doc.payload.data();
                    return { id: doc.payload.id, ...data };
                  })
                );

              reads$.push(itemData);
            } else {
              reads$.push(of([]));
            }
          }

          return combineLatest(reads$);
        }),
        map(joins => {
          const newArray = likedPosts.map((v, i) => {
            totalJoins += joins[i].length;
            return { ...v, ['postId']: joins[i] || null };
          });
          return newArray.sort((a, b) => {
            const d1 = new Date(a.dateLiked);
            const d2 = new Date(b.dateLiked);
            return d1 > d2 ? -1 : d2 > d1 ? 1 : 0;
          });
        })
      );
    });
};

export const leftJoin = (afs: AngularFirestore, field, collection, limit = 100) => {
  return source =>
    defer(() => {
      // Operator state
      let collectionData;

      // Track total num of joined doc reads
      let totalJoins = 0;

      return source.pipe(
        switchMap(data => {
          // Clear mapping on each emitted val ;

          // Save the parent data state
          collectionData = data as any[];

          const reads$ = [];
          for (const doc of collectionData) {
            // Push doc read to Array

            if (doc[field]) {
              // Perform query on join key, with optional limit
              const q = ref => ref.where(field, '==', doc[field]).limit(limit);

              reads$.push(afs.collection(collection, q).valueChanges());
            } else {
              reads$.push(of([]));
            }
          }

          return combineLatest(reads$);
        }),
        map(joins => {
          return collectionData.map((v, i) => {
            totalJoins += joins[i].length;
            return { ...v, [collection]: joins[i] || null };
          });
        })
      );
    });
};

export const leftJoinArray = (afs: AngularFirestore, field, collection) => {
  return source =>
    defer(() => {
      // Operator state
      let collectionData;
      const cache = new Map();

      return source.pipe(
        switchMap((data: any) => {
          cache.clear();
          collectionData = data as any[];
          const reads$ = [];
          let i = 0;
          if (!collectionData.length) {
            return of([]);
          }
          for (const doc of collectionData) {
            let array = doc[field];

            for (const item of array) {
              if (!item || cache.get(item || typeof item != 'string')) {
                continue;
              }
              const itemData = afs
                .collection(collection)
                .doc(item)
                .snapshotChanges()
                .pipe(
                  map(doc => {
                    const data: any = doc.payload.data();
                    return { id: doc.payload.id, ...data };
                  })
                );

              reads$.push(itemData);
              cache.set(item, i);
              i++;
            }
          }

          return reads$.length ? combineLatest(reads$) : of([]);
        }),
        map((joins: any) => {
          return collectionData.map((data, i) => {
            data[field] = data[field].map((v, i) => {
              const joinIdx = cache.get(v);
              return joins[joinIdx];
            });
            return {
              ...data,
              [field]: data[field] || null,
            };
          });
        })
      );
    });
};

/**
 *  한개의 post안에 userId 아이디를 가지고 있고 userId(글쓴이)의 정보(user document)를 불러와서 엮는 경우
 *  userId(field)가 default값이 되어야합니다.
 * @param afs
 * @param field
 * @param collection
 */
export const categoryDocJoin = (afs: AngularFirestore) => {
  return source =>
    defer(() => {
      let parent;
      return source.pipe(
        switchMap(data => {
          parent = data;

          let reads$ = [];

          for (let index = 0; index < 2; index++) {
            const id = index == 0 ? data['topCategory'] : data['subCategory'];

            let tmp$ = afs
              .doc(`category/${id}`)
              .snapshotChanges()
              .pipe(
                map(doc => {
                  return doc.payload.data();
                })
              );

            reads$.push(tmp$);
          }

          return reads$.length ? combineLatest(reads$) : of([]);
        }),
        map(fieldData => {
          delete parent['topCategory'];
          delete parent['subCategory'];
          return {
            topCategory: fieldData[0],
            subCategory: fieldData[1],
            ...parent,
          };
        })
      );
    });
};
